import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  selectUpgradeState,
  setCurrency,
  setSeatCount,
} from '@/app/redux/slices/upgradeSlice';
import getDefaultDashboardRoute from '@/common/utils/defaultDashboardRoute';
import { getCurrencyFromLocation } from '@/common/utils/license';
import { isUserAdmin } from '@/common/utils/organisation';
import PricingTable from '@/dashboard/Upgrade/PricingTable';
import { Alert } from '@shadcn/components/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { AlertCircleIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
export default function PricingTableView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { organisation } = useOrganisation();

  const { seatCount, currency } = useAppSelector(selectUpgradeState);
  const userId = useAppSelector(selectUserId);

  const studentCount = organisation?.studentCount || 1;
  const isAuthed = !!userId;
  const isAdmin = isUserAdmin(organisation, userId);

  useEffect(() => {
    if (seatCount === 0) {
      dispatch(setSeatCount(studentCount));
    }
  }, [dispatch, seatCount, studentCount]);

  useEffect(() => {
    getCurrencyFromLocation().then((currency) => {
      dispatch(setCurrency(currency));
    });
  }, [dispatch]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 md:p-8 bg-neutral-100">
      <div className="w-full max-w-7xl">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 w-full text-neutral-700 tracking-tight">
          <img
            src="/ui/new-spellcaster-logo.svg"
            alt="Spellcaster"
            className="max-w-[300px] sm:max-w-[200px] md:max-w-[300px] cursor-pointer"
            onClick={() => navigate(getDefaultDashboardRoute())}
          />

          <div className="flex w-full items-center justify-between px-2 pb-2 sm:p-0 sm:-mb-2">
            <span className="text-xl md:text-2xl font-medium flex gap-2 items-center">
              <span>Set up your plan</span>
            </span>

            {isAuthed && !isAdmin && (
              <Alert variant="warning" className="w-fit flex mx-auto py-2">
                <AlertCircleIcon className="size-4 ml-2" />
                <span className="mr-1">
                  Note: Payment can only be completed by your organisation's
                  administrator(s)
                </span>
              </Alert>
            )}

            <Select
              value={currency}
              onValueChange={(value) =>
                dispatch(setCurrency(value as 'USD' | 'AUD' | 'NZD'))
              }
            >
              <SelectTrigger className="w-fit bg-white mb-2">
                <SelectValue placeholder="Select a currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AUD">
                  <div className="flex items-center gap-2">
                    <Flag country="au" />
                    <span className="ml-2">AUD</span>
                  </div>
                </SelectItem>
                <SelectItem value="NZD">
                  <div className="flex items-center gap-2">
                    <Flag country="nz" />
                    <span className="ml-2">NZD</span>
                  </div>
                </SelectItem>
                <SelectItem value="USD">
                  <div className="flex items-center gap-2">
                    <Flag country="us" />
                    <span className="ml-2">USD</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="w-full h-full bg-white p-4 md:p-8 rounded-lg shadow-lg border border-border">
            <PricingTable />
          </div>
        </div>
      </div>
    </div>
  );
}

const Flag = styled.span<{ country: 'us' | 'au' | 'nz' }>`
  display: inline-block;
  width: 1.333333em;
  height: 1em;
  background: center / contain no-repeat
    url(${(props) => `/icons/flag-${props.country}.svg`});
`;
